import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle, Download, X,
} from 'lucide-react';
import {
  Button, Card, CountrySelect, EmptyState, OperatorSelect, PageHeader, PageSpinner,
  TBody, TD, TH, THead, TR, Table,
} from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';
import { downloadCsv } from '@/lib/utils/csvExport';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RiskEvent {
  transactionId: string;
  productCode: string;
  metadataId: string | null;
  sessionId: string | null;
  mobileNumberMasked: string | null;
  operator: string | null;
  countryCode: string | null;
  riskScore: number;
  trustScore: number;
  riskLevel: string;
  decision: string;
  reasonCodes: string[] | null;
  signals: Record<string, unknown> | null;
  devicePlatform: string | null;
  deviceRooted: boolean | null;
  emulatorDetected: boolean | null;
  vpnDetected: boolean | null;
  countryMismatch: boolean | null;
  createdAt: string;
}

interface RiskEventsPage {
  content: RiskEvent[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface Filters {
  clientId: string;
  productCode: string;
  riskLevel: string;
  decision: string;
  operator: string;
  countryCode: string;
  from: string;
  to: string;
}

// ── Risk level style ──────────────────────────────────────────────────────────

function RiskLevelBadge({ level }: { level: string }) {
  const map: Record<string, string> = {
    LOW: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    HIGH: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
    CRITICAL: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', map[level] ?? 'bg-slate-100 text-slate-600')}>
      {level}
    </span>
  );
}

function DecisionBadge({ decision }: { decision: string }) {
  const map: Record<string, string> = {
    ALLOW: 'text-emerald-600 dark:text-emerald-400',
    CHALLENGE: 'text-amber-600 dark:text-amber-400',
    REVIEW: 'text-orange-600 dark:text-orange-400',
    BLOCK: 'text-rose-600 dark:text-rose-400',
  };
  return <span className={cn('text-xs font-semibold', map[decision] ?? 'text-fg-muted')}>{decision}</span>;
}

// ── API ───────────────────────────────────────────────────────────────────────

function toInstant(date: string, end = false) {
  if (!date) return undefined;
  return `${date}T${end ? '23:59:59' : '00:00:00'}Z`;
}

async function fetchRiskEvents(filters: Filters, page: number): Promise<RiskEventsPage> {
  const params: Record<string, unknown> = { page, size: 25 };
  if (filters.clientId) params.clientId = filters.clientId;
  if (filters.productCode) params.productCode = filters.productCode;
  if (filters.riskLevel) params.riskLevel = filters.riskLevel;
  if (filters.decision) params.decision = filters.decision;
  if (filters.operator) params.operator = filters.operator;
  if (filters.countryCode) params.countryCode = filters.countryCode;
  const from = toInstant(filters.from);
  const to = toInstant(filters.to, true);
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await api.get<ResponseEnvelope<RiskEventsPage>>(Endpoints.adminRiskEvents.list, { params });
  return unwrap(res);
}

// ── Detail drawer ─────────────────────────────────────────────────────────────

function RiskEventDrawer({ event, onClose }: { event: RiskEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg overflow-y-auto bg-surface shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-fg">Risk Event Detail</p>
            <p className="font-mono text-[11px] text-fg-muted">{event.transactionId}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-fg-muted hover:bg-surface-muted hover:text-fg">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Product', event.productCode],
              ['Risk Level', <RiskLevelBadge key="rl" level={event.riskLevel} />],
              ['Decision', <DecisionBadge key="d" decision={event.decision} />],
              ['Risk Score', <span key="rs" className="font-bold text-fg">{event.riskScore}/100</span>],
              ['Trust Score', <span key="ts" className="font-bold text-emerald-600">{event.trustScore}/100</span>],
              ['Mobile', event.mobileNumberMasked ?? '—'],
              ['Operator', event.operator ?? '—'],
              ['Country', event.countryCode ?? '—'],
              ['Platform', event.devicePlatform ?? '—'],
              ['Timestamp', new Date(event.createdAt).toLocaleString()],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-xl border border-border bg-surface-muted/40 p-3">
                <p className="text-[10px] uppercase tracking-widest text-fg-subtle">{label}</p>
                <div className="mt-0.5 text-sm text-fg">{value}</div>
              </div>
            ))}
          </div>

          {/* Flags */}
          <div>
            <p className="mb-2 text-xs font-medium text-fg-muted">Device Flags</p>
            <div className="flex flex-wrap gap-2">
              {event.deviceRooted && <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Rooted Device</span>}
              {event.emulatorDetected && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Emulator</span>}
              {event.vpnDetected && <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">VPN</span>}
              {event.countryMismatch && <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">Country Mismatch</span>}
              {!event.deviceRooted && !event.emulatorDetected && !event.vpnDetected && !event.countryMismatch && (
                <span className="text-xs text-fg-muted">No device flags.</span>
              )}
            </div>
          </div>

          {/* Reason codes */}
          {event.reasonCodes && event.reasonCodes.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-fg-muted">Reason Codes</p>
              <div className="flex flex-wrap gap-1.5">
                {event.reasonCodes.map((code) => (
                  <span key={code} className="rounded-md bg-surface-muted px-2 py-0.5 font-mono text-[11px] text-fg">
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Signals */}
          {event.signals && Object.keys(event.signals).length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-fg-muted">Signals</p>
              <div className="rounded-xl bg-surface-muted p-3 font-mono text-[11px] text-fg">
                {Object.entries(event.signals).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-fg-muted">{k}</span>
                    <span className="text-fg">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AdminRiskEventsPage() {
  const [filters, setFilters] = useState<Filters>({
    clientId: '', productCode: '', riskLevel: '', decision: '',
    operator: '', countryCode: '', from: '', to: '',
  });
  const [applied, setApplied] = useState<Filters>({ ...filters });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<RiskEvent | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'risk-events', applied, page],
    queryFn: () => fetchRiskEvents(applied, page),
    staleTime: 0,
  });

  function apply() { setApplied({ ...filters }); setPage(0); }
  function reset() {
    const empty: Filters = { clientId: '', productCode: '', riskLevel: '', decision: '', operator: '', countryCode: '', from: '', to: '' };
    setFilters(empty); setApplied(empty); setPage(0);
  }

  async function doExport() {
    setIsExporting(true);
    try {
      const res = await fetchRiskEvents(applied, 0);
      const headers = ['Transaction ID', 'Product', 'Risk Level', 'Decision', 'Risk Score', 'Trust Score', 'Mobile', 'Operator', 'Country', 'Timestamp'];
      const rows = res.content.map((e) => [
        e.transactionId, e.productCode, e.riskLevel, e.decision,
        e.riskScore, e.trustScore, e.mobileNumberMasked ?? '',
        e.operator ?? '', e.countryCode ?? '', new Date(e.createdAt).toLocaleString(),
      ]);
      downloadCsv(`risk-events-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
    } finally { setIsExporting(false); }
  }

  const hasActiveFilters = Object.values(applied).some(Boolean);
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Risk Events"
        subtitle="Shield, Detect, and Trust assessments across all clients."
        actions={
          <Button variant="outline" size="sm" onClick={doExport} disabled={isExporting}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <input type="text" placeholder="Client ID"
            value={filters.clientId}
            onChange={(e) => setFilters((f) => ({ ...f, clientId: e.target.value }))}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
          <select value={filters.productCode}
            onChange={(e) => setFilters((f) => ({ ...f, productCode: e.target.value }))}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30">
            <option value="">All Products</option>
            {['VERIFY', 'VERIFY_PLUS', 'SIGNAL', 'SHIELD', 'DETECT', 'TRUST'].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select value={filters.riskLevel}
            onChange={(e) => setFilters((f) => ({ ...f, riskLevel: e.target.value }))}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30">
            <option value="">All Risk Levels</option>
            {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={filters.decision}
            onChange={(e) => setFilters((f) => ({ ...f, decision: e.target.value }))}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30">
            <option value="">All Decisions</option>
            {['ALLOW', 'CHALLENGE', 'REVIEW', 'BLOCK'].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <CountrySelect value={filters.countryCode}
            onChange={(c) => setFilters((f) => ({ ...f, countryCode: c, operator: '' }))} />
          <OperatorSelect value={filters.operator}
            onChange={(o) => setFilters((f) => ({ ...f, operator: o }))}
            countryCode={filters.countryCode || undefined} />
          <input type="date" value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
          <input type="date" value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          {hasActiveFilters && (
            <button onClick={reset} className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg">
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
          <Button size="sm" onClick={apply}>Apply Filters</Button>
        </div>
      </Card>

      {/* Table */}
      <Card noPadding>
        {isLoading ? <div className="py-12"><PageSpinner /></div>
          : isError ? (
            <div className="px-5 py-8">
              <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                <p className="text-rose-700 dark:text-rose-300">
                  {isApiError(error) ? error.firstErrorMessage : 'Failed to load risk events.'}
                </p>
              </div>
            </div>
          ) : data?.content.length === 0 ? (
            <EmptyState message="No risk events match the current filters." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Transaction ID</TH>
                  <TH>Product</TH>
                  <TH>Risk Level</TH>
                  <TH>Decision</TH>
                  <TH>Score</TH>
                  <TH>Mobile</TH>
                  <TH>Country</TH>
                  <TH>Timestamp</TH>
                </TR>
              </THead>
              <TBody>
                {data?.content.map((event) => (
                  <TR key={event.transactionId}
                      className="cursor-pointer hover:bg-surface-muted/50"
                      onClick={() => setSelected(event)}>
                    <TD className="font-mono text-[11px] text-fg-muted">{event.transactionId}</TD>
                    <TD className="text-xs font-medium text-fg">{event.productCode}</TD>
                    <TD><RiskLevelBadge level={event.riskLevel} /></TD>
                    <TD><DecisionBadge decision={event.decision} /></TD>
                    <TD className="text-xs">
                      <span className="font-semibold text-fg">{event.riskScore}</span>
                      <span className="text-fg-muted"> / 100</span>
                    </TD>
                    <TD className="font-mono text-xs text-fg-muted">{event.mobileNumberMasked ?? '—'}</TD>
                    <TD className="text-xs text-fg-muted">{event.countryCode ?? '—'}</TD>
                    <TD className="text-[11px] text-fg-muted">
                      {new Date(event.createdAt).toLocaleString()}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

        {/* Pagination */}
        {totalPages > 1 && !isLoading && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <span className="text-xs text-fg-muted">
              {data?.totalElements?.toLocaleString()} events · Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      {selected && <RiskEventDrawer event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
